/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import { Segment } from '@mairistem/composants';
import { Bloc } from '@mairistem/administres-utils';

import { getSyntheseDashboard } from '@/routes';
import { COLORS } from '@/constants';
import { useEntiteStore } from '@/zustand';
import { RecenseModal } from '@/components/Dashboard/Liste';

interface VueSynthetiqueProps {
  style?: React.CSSProperties;
  setLoading: (loading: boolean) => void;
}

const VueSynthetique = ({ setLoading, style }: VueSynthetiqueProps) => {
  const [data, setData] = React.useState<Record<string, number>>();
  const [openModal, setOpenModal] = React.useState(false);
  const blocked = useEntiteStore((state) => state.blocked);
  const [hoverDoubleNat, setHoverDoubleNat] = React.useState(false);

  React.useEffect(() => {
    if (!blocked) {
      setLoading(true);
      request
        .get(getSyntheseDashboard())
        .then((response: Record<string, number>) => {
          setData(response);
        })
        .catch(() => {
          toast(
            'Une erreur est survenue lors de la récupération de la synthèse',
            { type: 'error' },
          );
        })
        .finally(() => setLoading(false));
    }
  }, [blocked, setLoading]);

  const segmentStyle = {
    backgroundColor: COLORS.GreyDashboardColor,
    border: 'none',
    boxShadow: 'none',
    margin: 0,
    flex: 1,
  };

  const clickableSegmentStyle = {
    ...segmentStyle,
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  };

  const doubleNatSegmentStyle = {
    ...clickableSegmentStyle,
    transform: hoverDoubleNat ? 'scale(1.02)' : 'scale(1)',
    boxShadow: hoverDoubleNat ? '0 12px 30px rgba(0, 0, 0, 0.18)' : 'none',
  };

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  };

  const badgeStyle = {
    fontWeight: '600',
    fontSize: '2.5em',
    padding: '10px',
    borderRadius: '4px',
    minWidth: '60px',
    textAlign: 'center',
  };

  const titleStyle = {
    fontWeight: '600',
  };

  const statsStyle = {
    fontSize: '0.8em',
    marginTop: '5px',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  };

  return (
    <Bloc
      icon="file text"
      header={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div>Vue synthétique</div>
        </div>
      }
      style={style}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          gap: '1em',
          marginTop: '1.5em',
        }}
      >
        <Segment style={segmentStyle}>
          <div style={containerStyle}>
            {/* @ts-expect-error Type error */}
            <span
              style={{
                ...badgeStyle,
                color: 'green',
                backgroundColor: 'lightgreen',
              }}
            >
              {(data?.hommeRecense || 0) + (data?.femmeRecense || 0)}
            </span>
            <div>
              <div style={titleStyle}>Nombre de jeunes recensés</div>
              {/* @ts-expect-error Type error */}
              <div style={statsStyle}>
                {data?.femmeRecense || 0} Filles&nbsp;&nbsp;&nbsp;
                {data?.hommeRecense || 0} Garçons
              </div>
            </div>
          </div>
        </Segment>
        <Segment style={segmentStyle}>
          <div style={containerStyle}>
            {/* @ts-expect-error Type error */}
            <span
              style={{
                ...badgeStyle,
                color: 'red',
                backgroundColor: COLORS.RedCardColor,
              }}
            >
              {(data?.hommeNonRecense || 0) + (data?.femmeNonRecense || 0)}
            </span>
            <div>
              <div style={titleStyle}>Nombre de jeunes non recensés</div>
              {/* @ts-expect-error Type error */}
              <div style={statsStyle}>
                {data?.femmeNonRecense || 0} Filles&nbsp;&nbsp;&nbsp;
                {data?.hommeNonRecense || 0} Garçons
              </div>
            </div>
          </div>
        </Segment>
        <Segment
          style={doubleNatSegmentStyle}
          onClick={() => setOpenModal(true)}
          onMouseEnter={() => setHoverDoubleNat(true)}
          onMouseLeave={() => setHoverDoubleNat(false)}
        >
          <div
            style={{ ...containerStyle, gap: '0.5em', alignItems: 'stretch' }}
          >
            {/* @ts-expect-error Type error */}
            <span
              style={{
                ...badgeStyle,
                color: 'orange',
                backgroundColor: COLORS.OrangeCardColor,
              }}
            >
              {(data?.hommeDoubleNationalite || 0) +
                (data?.femmeDoubleNationalite || 0)}
            </span>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <div
                  style={{
                    ...titleStyle,
                    whiteSpace: 'normal',
                    textDecoration: 'underline',
                  }}
                >
                  Jeunes ayant une double nationalité
                </div>
                {/* @ts-expect-error Type error */}
                <div style={statsStyle}>
                  {data?.femmeDoubleNationalite || 0} Filles&nbsp;&nbsp;&nbsp;
                  {data?.hommeDoubleNationalite || 0} Garçons
                </div>
              </div>
            </div>
          </div>
        </Segment>
      </div>

      <RecenseModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        doubleNationalite
      />
    </Bloc>
  );
};

export default VueSynthetique;
